import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import {
  confirmMedia,
  createCheckoutSession,
  deleteMedia,
  getEvent,
  getMediaUploadUrl,
  listEventMedia,
  listEventRegistrationsPublic,
  listMyVehicles,
  type EventRegistrationPublicRow,
  type EventRow,
  type MediaRow,
  type VehicleRow,
} from "../lib/api";
import { MediaViewer } from "../components/MediaViewer";
import { useDropUpload } from "../lib/useDropUpload";
import { CarFrontFillIcon, FileEarmarkArrowUpIcon, Trash3FillIcon } from "../components/icons";
import { formatEventDate } from "../lib/formatDate";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface RacerDraft {
  racerName: string;
  // UI convenience only (see GitHub issue #8's open design question) --
  // picks which saved vehicle this row is a reminder for while filling out
  // the form. Not sent anywhere: RacerInput/RegistrationRow have no
  // vehicleId field, so which vehicle actually raced isn't recorded.
  vehicleId: string;
}

function RegisterClass({
  event,
  racerClass,
  registrations,
  vehicles,
  onRegistered,
}: {
  event: EventRow;
  racerClass: string;
  registrations: EventRegistrationPublicRow[];
  vehicles: VehicleRow[];
  onRegistered: () => void;
}) {
  const { user } = useAuth();
  const { addItem } = useCart();
  const [drafts, setDrafts] = useState<RacerDraft[]>([{ racerName: user?.name ?? user?.email ?? "", vehicleId: "" }]);
  const [added, setAdded] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Only vehicles saved under this class are worth offering per class
  // section -- a 1/8 buggy isn't a relevant pick while registering for
  // 1/10 truggy.
  const classVehicles = vehicles.filter((v) => v.class === racerClass);

  function updateDraft(i: number, name: string) {
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, racerName: name } : d)));
  }
  function updateDraftVehicle(i: number, vehicleId: string) {
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, vehicleId } : d)));
  }
  function addAnotherRacer() {
    setDrafts((prev) => [...prev, { racerName: "", vehicleId: "" }]);
  }
  function removeDraft(i: number) {
    setDrafts((prev) => prev.filter((_, idx) => idx !== i));
  }

  function handleAddToCart() {
    drafts
      .filter((d) => d.racerName.trim().length > 0)
      .forEach((d) => {
        addItem({
          eventId: event.eventId,
          eventTitle: event.title,
          racerName: d.racerName.trim(),
          racerClass,
          entryFeeCents: event.entryFeeCents,
        });
      });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  // Registers immediately instead of going through the cart -- same
  // createCheckoutSession call CartPage's checkout button makes, just for
  // this class's racers right now. The registration row is created (status
  // "pending", or "paid" outright for a free event) the moment this call
  // succeeds, so they're in the race whether or not a Stripe redirect
  // follows to actually collect payment.
  async function handleRegister() {
    const racers = drafts.filter((d) => d.racerName.trim().length > 0).map((d) => ({ racerName: d.racerName.trim(), racerClass }));
    if (racers.length === 0) return;
    setRegistering(true);
    setRegisterError(null);
    try {
      const res = await createCheckoutSession({ eventId: event.eventId, racers });
      if (res.checkoutUrl) {
        window.location.assign(res.checkoutUrl);
        return;
      }
      setDrafts([{ racerName: "", vehicleId: "" }]);
      onRegistered();
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setRegistering(false);
    }
  }

  return (
    <div className="class-row">
      <div style={{ flex: "1 1 100%" }}>
        <strong>{racerClass}</strong> — {formatDollars(event.entryFeeCents)}
        <div className="racer-rows">
          {drafts.map((d, i) => (
            <div className="racer-row" key={i}>
              <input
                placeholder="Racer name"
                value={d.racerName}
                onChange={(e) => updateDraft(i, e.target.value)}
              />
              {classVehicles.length > 0 && (
                <label className="racer-vehicle-select">
                  <CarFrontFillIcon className="muted" />
                  <select value={d.vehicleId} onChange={(e) => updateDraftVehicle(i, e.target.value)}>
                    <option value="">Not Saved</option>
                    {classVehicles.map((v) => (
                      <option key={v.vehicleId} value={v.vehicleId}>
                        {v.manufacturer}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {drafts.length > 1 && (
                <button
                  type="button"
                  className="danger"
                  title="Remove racer"
                  aria-label="Remove racer"
                  style={{ padding: "0.3rem 0.4rem", display: "inline-flex", flex: "0 0 auto" }}
                  onClick={() => removeDraft(i)}
                >
                  <Trash3FillIcon />
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" className="secondary" onClick={addAnotherRacer}>
            + Add another racer
          </button>
          <button type="button" onClick={handleRegister} disabled={registering}>
            {registering ? "Registering…" : "Register"}
          </button>
          <button type="button" className="success" onClick={handleAddToCart}>
            {added ? "Added!" : "Add to cart"}
          </button>
        </div>
        {registerError && <p className="err">{registerError}</p>}

        {registrations.length > 0 && (
          <div className="table-scroll"><table style={{ marginTop: "0.75rem" }}>
            <thead>
              <tr>
                <th>Racer</th>
                <th>Paid</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((r, i) => (
                <tr key={i}>
                  <td>{r.racerName}</td>
                  <td>
                    <span className={`pill ${r.paid ? "success" : "warn"}`}>{r.paid ? "Paid" : "Pending"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function MediaGallery({ event }: { event: EventRow }) {
  const { user } = useAuth();
  const [media, setMedia] = useState<MediaRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [viewing, setViewing] = useState<MediaRow | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    listEventMedia(event.eventId)
      .then((res) => setMedia(res.media))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load media."));
  }, [event.eventId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Uploads sequentially (one PUT + confirm at a time) rather than in
  // parallel, so the progress count stays accurate and a slow connection
  // doesn't fire a dozen concurrent presigned-URL requests at once.
  const uploadFiles = useCallback(
    async (files: File[]) => {
      const list = files.filter((f) => /^(image|video)\//.test(f.type));
      if (!list.length) return;
      setError(null);
      let done = 0;
      setUploadMsg(`Uploading 0/${list.length}…`);
      try {
        for (const file of list) {
          const { uploadUrl, mediaId, s3Key } = await getMediaUploadUrl(event.eventId, file.name, file.type);
          const putRes = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
          });
          if (!putRes.ok) throw new Error(`Upload failed for ${file.name}.`);
          const type = file.type.startsWith("video") ? "video" : "photo";
          await confirmMedia(event.eventId, { mediaId, s3Key, type });
          done++;
          setUploadMsg(`Uploading ${done}/${list.length}…`);
        }
        setUploadMsg(`Uploaded ${done} file${done === 1 ? "" : "s"}.`);
        refresh();
        setTimeout(() => setUploadMsg(null), 4000);
      } catch (err) {
        setUploadMsg(null);
        setError(err instanceof Error ? err.message : "Upload failed.");
        refresh();
      }
    },
    [event.eventId, refresh],
  );

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files;
    if (!picked || !picked.length) return;
    const list = Array.from(picked);
    e.target.value = "";
    await uploadFiles(list);
  }

  const { isDragging, dropProps } = useDropUpload(uploadFiles);

  async function handleDelete(mediaId: string) {
    try {
      await deleteMedia(event.eventId, mediaId);
      setMedia((prev) => prev?.filter((m) => m.mediaId !== mediaId) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  return (
    <div className="card media-drop-zone" {...(user ? dropProps : {})}>
      {user && isDragging && (
        <div className="media-drop-overlay">
          <div className="media-drop-overlay-text">Drop to upload to {event.title}</div>
        </div>
      )}
      <h3>Photos &amp; videos</h3>
      {error && <p className="err">{error}</p>}
      {user ? (
        <div>
          <input
            id="media-upload"
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileChange}
            disabled={!!uploadMsg}
            style={{ display: "none" }}
          />
          <button
            type="button"
            className="share-media-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!uploadMsg}
          >
            <FileEarmarkArrowUpIcon /> Share Media
          </button>
          <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.3rem" }}>or drag &amp; drop photos/videos here</p>
          {uploadMsg && <p className="muted">{uploadMsg}</p>}
        </div>
      ) : (
        <p className="muted">
          <Link to="/login">Sign in</Link> to share your own photos and videos.
        </p>
      )}
      <div className="media-grid">
        {media?.map((m) => {
          const canDelete = user?.isAdmin || m.uploaderId === user?.sub;
          const src = m.url ?? m.s3Key;
          return (
            <div className="media-item" key={m.mediaId}>
              {m.type === "video" ? (
                <video src={src} muted preload="metadata" onClick={() => setViewing(m)} />
              ) : (
                <img src={src} alt={m.caption ?? ""} onClick={() => setViewing(m)} />
              )}
              {canDelete && (
                <button className="danger media-delete" onClick={() => handleDelete(m.mediaId)}>
                  Delete
                </button>
              )}
              {m.caption && <div className="media-caption">{m.caption}</div>}
            </div>
          );
        })}
      </div>
      {media && media.length === 0 && <p className="muted">No media yet — be the first to share one.</p>}
      {viewing && (
        <MediaViewer
          url={viewing.url ?? viewing.s3Key}
          name={viewing.caption ?? "Media"}
          type={viewing.type}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [registrations, setRegistrations] = useState<EventRegistrationPublicRow[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    getEvent(eventId)
      .then((res) => setEvent(res.event))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load event."));
  }, [eventId]);

  const refreshRegistrations = useCallback(() => {
    if (!eventId) return;
    listEventRegistrationsPublic(eventId)
      .then((res) => setRegistrations(res.registrations))
      .catch(() => {});
  }, [eventId]);

  useEffect(refreshRegistrations, [refreshRegistrations]);

  // Shared across every class section below -- fetched once here rather
  // than per RegisterClass instance, same reason registrations is lifted
  // up too.
  useEffect(() => {
    if (!user) return;
    listMyVehicles()
      .then((res) => setVehicles(res.vehicles))
      .catch(() => {});
  }, [user]);

  if (error) return <p className="err">{error}</p>;
  if (!event) return <p className="muted">Loading…</p>;

  const now = Date.now();
  const opensAt = event.registrationOpensAt ? new Date(event.registrationOpensAt).getTime() : null;
  const closesAt = event.registrationClosesAt ? new Date(event.registrationClosesAt).getTime() : null;
  const registrationOpen = (!opensAt || now >= opensAt) && (!closesAt || now <= closesAt);

  return (
    <div>
      <p>
        <Link to="/">&larr; All events</Link>
      </p>
      <h1>{event.title}</h1>
      <p className="muted">
        {formatEventDate(event.dateISO, event.endDateISO, { withTime: true })} {event.location && `· ${event.location}`}
      </p>
      {event.description && <p>{event.description}</p>}
      {(event.registrationOpensAt || event.registrationClosesAt) && (
        <p className="muted">
          Registration {event.registrationOpensAt && `opens ${new Date(event.registrationOpensAt).toLocaleString()}`}
          {event.registrationOpensAt && event.registrationClosesAt && " · "}
          {event.registrationClosesAt && `closes ${new Date(event.registrationClosesAt).toLocaleString()}`}
        </p>
      )}

      <div className="card">
        <h3>Register</h3>
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : !user ? (
          <p>
            <button onClick={() => navigate("/login")}>Sign in to register</button>
          </p>
        ) : !user.isVerifiedMember ? (
          <p className="notice">Your account is pending verification by a club admin.</p>
        ) : !registrationOpen ? (
          <p className="muted">Registration is not currently open for this event.</p>
        ) : (
          <div className="classes-list">
            {(event.classes ?? []).map((c) => (
              <RegisterClass
                key={c.name}
                event={event}
                racerClass={c.name}
                registrations={registrations.filter((r) => r.racerClass === c.name)}
                vehicles={vehicles}
                onRegistered={refreshRegistrations}
              />
            ))}
            {(event.classes ?? []).length === 0 && <p className="muted">No classes have been set up yet.</p>}
          </div>
        )}
      </div>

      <MediaGallery event={event} />
    </div>
  );
}
